import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';

interface HistoryItem {
  id: number;
  deposit_order_id: number;
  old_status: number;
  new_status: number;
  updated_by: string | null;
  updated_at: string;
  amount: string;
  fee: string;
  payAmount: string;
  extra: string;
  domain: string;
}

interface GroupedHistoryItem {
  date: string;
  domain: string;
  timeRange: string;
  items: HistoryItem[];
  totalAmount: number;
  totalFee: number;
  totalPayAmount: number;
}

const fetchAllStatusPaidHistory = async (domain: string | null = null) => {
  try {
    const response = await fetch(
      `https://azpay.devng.pro/api/transactions/status-paid-history/all${domain ? `?domain=${domain}` : ''}`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let result = '';
    
    while (true) {
      const {value, done} = await reader.read();
      if (done) break;
      result += decoder.decode(value, {stream: true});
    }
    
    return JSON.parse(result);
  } catch (error) {
    console.error('Error fetching status paid history:', error);
    throw error;
  }
};

const HistoryPaid: React.FC = () => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [groupedHistory, setGroupedHistory] = useState<GroupedHistoryItem[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [domains, setDomains] = useState<string[]>([]);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const fetchHistory = useCallback(async (domain: string) => {
    setLoading(true);
    setLoadingProgress(0);
    try {
      const response = await fetchAllStatusPaidHistory(domain || null);
      setHistory(response.data);
      setError(null);

      const uniqueDomains = Array.from(new Set(response.data.map((item: HistoryItem) => item.domain)));
      setDomains(uniqueDomains as string[]);
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingProgress(100);
    }
  }, []); 

  const LoadingSpinner = () => (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl flex flex-col items-center">
        <div className="w-full max-w-xs bg-gray-200 rounded-full h-2.5 mb-4">
          <div 
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
            style={{ width: `${loadingProgress}%` }}
          ></div>
        </div>
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-gray-700 text-lg font-medium">กำลังโหลดข้อมูล...</div>
        <div className="text-gray-500 text-sm mt-2">
          {loadingProgress < 100 ? 'กรุณารอสักครู่' : 'กำลังประมวลผล'}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    fetchHistory(selectedDomain);
  }, [selectedDomain, fetchHistory]);

  const handleDomainChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDomain(event.target.value);
  };

  const groupHistoryItems = useCallback((items: HistoryItem[]) => {
    const grouped = items.reduce((acc: { [key: string]: GroupedHistoryItem }, item) => {
      const date = format(new Date(item.updated_at), 'yyyy-MM-dd');
      const timeRange = format(new Date(item.updated_at), 'HH:mm');
      const key = `${date}-${item.domain}-${timeRange}`;
      
      if (!acc[key]) {
        acc[key] = {
          date,
          domain: item.domain,
          timeRange,
          items: [],
          totalAmount: 0,
          totalFee: 0,
          totalPayAmount: 0
        };
      }
      
      acc[key].items.push(item);
      acc[key].totalAmount += parseFloat(item.amount);
      acc[key].totalFee += parseFloat(item.fee);
      acc[key].totalPayAmount += parseFloat(item.payAmount);
      
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => {
      const dateTimeA = `${a.date} ${a.timeRange}`;
      const dateTimeB = `${b.date} ${b.timeRange}`;
      return new Date(dateTimeB).getTime() - new Date(dateTimeA).getTime();
    });
  }, []);

  useEffect(() => {
    if (history.length > 0) {
      const grouped = groupHistoryItems(history);
      setGroupedHistory(grouped);
    }
  }, [history, groupHistoryItems]);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">ประวัติการอัพเดทสถานะการเบิกเงิน</h1>
      
      <div className="mb-4">
        <label htmlFor="domain-select" className="block text-sm font-medium text-gray-700">เลือกโดเมน</label>
        <select
          id="domain-select"
          value={selectedDomain}
          onChange={handleDomainChange}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
        >
          <option value="">ทั้งหมด</option>
          {domains.map((domain) => (
            <option key={domain} value={domain}>{domain}</option>
          ))}
        </select>
      </div>
      
      {loading && <LoadingSpinner />}
      {error && <div className="text-red-500 text-center">{error}</div>}
      
      {!loading && !error && (
        <div className="space-y-4">
          {groupedHistory.map((group) => (
            <div key={`${group.date}-${group.domain}-${group.timeRange}`} className="bg-white shadow-md rounded-lg overflow-hidden">
              <div
                onClick={() => toggleGroup(`${group.date}-${group.domain}-${group.timeRange}`)}
                className="px-6 py-4 bg-gray-50 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
              >
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold">
                    {format(new Date(group.date), 'dd MMMM yyyy', { locale: th })}
                    <span className="ml-2 text-sm text-gray-600">
                      เวลา {group.timeRange} น.
                    </span>
                  </h3>
                  <p className="text-sm text-gray-600">โดเมน: {group.domain}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">จำนวนรายการ: {group.items.length}</p>
                  <p className="font-semibold">
                    ยอดรวม: {group.totalPayAmount.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                  </p>
                </div>
              </div>
              
              {expandedGroups.includes(`${group.date}-${group.domain}-${group.timeRange}`) && (
                <div className="p-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนเงิน</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ค่าธรรมเนียม</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ยอดสุทธิ</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {group.items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.deposit_order_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(item.amount).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(item.fee).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(item.payAmount).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryPaid;
